import React, { useState, useEffect, useRef } from 'react';
import { X, Loader, Search, Plus, Trash2 } from 'lucide-react';
import ColorInput from '../../components/common/ColorInput';
import ColorDot from '../../components/common/ColorDot';
import SearchableSelect from '../../components/common/SearchableSelect';
import { resolveColorFromLookup } from '../../utils/colorUtils';
import { customerPortalService } from '../../services/customerPortalApi';
import toast from 'react-hot-toast';

const CreateOrderForm = ({ onClose, onSuccess, orderToEdit = null }) => {
  const [loading, setLoading] = useState(false);
  const [stockCards, setStockCards] = useState([]);
  const [loadingStockCards, setLoadingStockCards] = useState(true);
  const [showMediantegSearch, setShowMediantegSearch] = useState(false);
  const [mediantegSearchTerm, setMediantegSearchTerm] = useState('');
  const [mediantegSearchResults, setMediantegSearchResults] = useState([]);
  const [mediantegAllOrders, setMediantegAllOrders] = useState([]); // Tüm aktif siparişler (frontend arama için)
  const [searchingMedianteg, setSearchingMedianteg] = useState(false);
  /** Janes sipariş satırına tıklanınca detay API — çift tıklama ve bekleme geri bildirimi */
  const [mediantegOrderDetailLoading, setMediantegOrderDetailLoading] = useState(false);
  const mediantegOrderDetailInFlightRef = useRef(false);

  // Çoklu renk/miktar desteği için formData yapısı
  const [formData, setFormData] = useState({
    stockCardId: '',
    unitId: '',
    mediantegOrderNo: '',
    mediantegModelNo: '',
    mediantegOrderType: '',
    mediantegImageUrl: '',
    description: '',
            items: [{ color: '', quantity: '', imageUrl: '' }] // Her renk için miktar ve görsel
  });

  const [errors, setErrors] = useState({});
  const isEditMode = !!orderToEdit;

  // Aktif stok kartlarını yükle
  useEffect(() => {
    loadStockCards();
  }, []);

  // Düzenleme modunda sipariş verilerini yükle
  useEffect(() => {
    if (orderToEdit && stockCards.length > 0) {
      // stockCardId'yi orderToEdit.stockCardId veya orderToEdit.stockCard?.id'den al
      const stockCardId = orderToEdit.stockCardId || orderToEdit.stockCard?.id;
      // unitId'yi orderToEdit.unitId veya orderToEdit.unit?.id veya orderToEdit.stockCard?.unitId'den al
      const unitId = orderToEdit.unitId || orderToEdit.unit?.id || orderToEdit.stockCard?.unitId;
      
      // stockCardId'yi number olarak tut (SearchableSelect getOptionValue number döndürüyor)
      const stockCardIdValue = stockCardId ? parseInt(stockCardId) : '';
      
      setFormData({
        stockCardId: stockCardIdValue,
        unitId: unitId ? parseInt(unitId) : '',
        mediantegOrderNo: orderToEdit.mediantegOrderNo || '',
        mediantegModelNo: orderToEdit.mediantegModelNo || '',
        mediantegOrderType: orderToEdit.mediantegOrderType || '',
        mediantegImageUrl: orderToEdit.mediantegImageUrl || '',
        description: orderToEdit.description || '',
        items: orderToEdit.color ? [{ 
          color: orderToEdit.color, 
          quantity: orderToEdit.quantity?.toString() || '', 
          imageUrl: orderToEdit.mediantegImageUrl || '' 
        }] : [{ color: '', quantity: '', imageUrl: '' }]
      });
      
      console.log('[CreateOrderForm] formData set edildi, stockCardId:', stockCardIdValue);
    }
  }, [orderToEdit, stockCards]);

  // Medianteg arama modalı açıldığında aktif siparişleri çek
  useEffect(() => {
    if (showMediantegSearch && !isEditMode) {
      loadMediantegOrdersOnOpen();
    }
  }, [showMediantegSearch, isEditMode]);

  // Modal açıldığında Medianteg siparişlerini çek
  const loadMediantegOrdersOnOpen = async () => {
    setSearchingMedianteg(true);
    try {
      const response = await customerPortalService.get('/orders/medianteg/active');
      if (response.data.success) {
        const orders = response.data.data || [];
        // Tüm aktif siparişleri sakla (frontend arama için)
        setMediantegAllOrders(orders);
        // İlk başta tüm siparişleri göster
        setMediantegSearchResults(orders);
      }
    } catch (error) {
      console.error('Medianteg aktif siparişleri yüklenemedi:', error);
      setMediantegAllOrders([]);
      setMediantegSearchResults([]);
    } finally {
      setSearchingMedianteg(false);
    }
  };

  const loadStockCards = async () => {
    try {
      const response = await customerPortalService.get('/stock-cards/active');
      if (response.data.success) {
        setStockCards(response.data.data || []);
      } else {
        throw new Error(response.data.message || 'Stok kartları yüklenemedi');
      }
    } catch (error) {
      console.error('Stok kartları yüklenemedi:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Stok kartları yüklenemedi';
      toast.error(errorMessage);
    } finally {
      setLoadingStockCards(false);
    }
  };

  // Medianteg sipariş ara (frontend'de filtreleme)
  const searchMediantegOrders = () => {
    const searchTerm = mediantegSearchTerm.trim().toLowerCase();
    
    if (!searchTerm) {
      // Arama terimi yoksa tüm siparişleri göster
      setMediantegSearchResults(mediantegAllOrders);
      return;
    }

    // Frontend'de filtreleme yap
    const filtered = mediantegAllOrders.filter(order => {
      const orderNo = (order.orderNo || '').toLowerCase();
      const modelNo = (order.modelNo || '').toLowerCase();
      return orderNo.includes(searchTerm) || modelNo.includes(searchTerm);
    });

    setMediantegSearchResults(filtered);
  };

  // Arama terimi değiştiğinde otomatik arama yap
  useEffect(() => {
    if (showMediantegSearch && mediantegAllOrders.length > 0) {
      searchMediantegOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediantegSearchTerm, showMediantegSearch, mediantegAllOrders]);

  // Medianteg sipariş detaylarını yükle ve formu doldur
  const loadMediantegOrderDetails = async (orderId) => {
    if (mediantegOrderDetailInFlightRef.current) return;
    mediantegOrderDetailInFlightRef.current = true;
    setMediantegOrderDetailLoading(true);
    try {
      const response = await customerPortalService.get(`/orders/medianteg/order/${orderId}`);
      if (response.data.success) {
        const orderDetails = response.data.data;
        
        // Stok kartını set et
        if (orderDetails.stockCard) {
          // stockCardId ve unitId'yi number olarak parse et (SearchableSelect için)
          const stockCardIdNum = orderDetails.stockCard.id ? parseInt(orderDetails.stockCard.id) : '';
          const unitIdNum = orderDetails.stockCard.unitId ? parseInt(orderDetails.stockCard.unitId) : '';
          
          setFormData(prev => ({
            ...prev,
            stockCardId: stockCardIdNum,
            unitId: unitIdNum,
            mediantegOrderNo: orderDetails.orderNo,
            mediantegModelNo: orderDetails.modelNo,
            mediantegOrderType: orderDetails.orderType || prev.mediantegOrderType || '',
            mediantegImageUrl: orderDetails.imageUrl,
            description: orderDetails.description || '',
            items: orderDetails.colors.map((colorItem) => ({
              color: resolveColorFromLookup(colorItem.color || ''),
              quantity: colorItem.quantity || '',
              imageUrl: colorItem.imageUrl || ''
            }))
          }));
          
          console.log('[CreateOrderForm] Medianteg sipariş yüklendi, stockCardId:', stockCardIdNum, 'unitId:', unitIdNum);
          
          toast.success('Sipariş bilgileri yüklendi');
          setShowMediantegSearch(false);
          setMediantegSearchTerm('');
          setMediantegSearchResults([]);
        } else {
          toast.error('Bu sipariş için eşleştirilmiş stok kartı bulunamadı');
        }
      } else {
        throw new Error(response.data.message || 'Sipariş detayları yüklenemedi');
      }
    } catch (error) {
      console.error('Medianteg sipariş detay hatası:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Sipariş detayları yüklenirken hata oluştu';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      mediantegOrderDetailInFlightRef.current = false;
      setMediantegOrderDetailLoading(false);
    }
  };

  const handleStockCardChange = (e) => {
    const stockCardId = e.target.value;
    const stockCardIdNum = stockCardId ? parseInt(stockCardId) : '';
    const selectedStockCard = stockCards.find(sc => sc.id === stockCardIdNum);
    
    setFormData({
      ...formData,
      stockCardId: stockCardIdNum,
      unitId: selectedStockCard?.unitId || selectedStockCard?.unit?.id || ''
    });
    
    if (errors.stockCardId) {
      setErrors({ ...errors, stockCardId: '' });
    }
  };

  // Renk/miktar ekle
  const addColorItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { color: '', quantity: '', imageUrl: '' }]
    });
  };

  // Renk/miktar sil
  const removeColorItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        items: newItems
      });
    }
  };

  // Renk/miktar güncelle
  const updateColorItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({
      ...formData,
      items: newItems
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validasyon
    const newErrors = {};
    if (!formData.stockCardId) newErrors.stockCardId = 'Stok kartı seçilmelidir';
    if (!formData.unitId) newErrors.unitId = 'Birim gereklidir';
    
    // Her item için validasyon
    formData.items.forEach((item, index) => {
      if (!item.color || !item.color.trim()) {
        newErrors[`items.${index}.color`] = 'Renk gereklidir';
      }
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        newErrors[`items.${index}.quantity`] = 'Geçerli bir miktar girilmelidir';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        // Düzenleme modu - tek sipariş güncelle
        const item = formData.items[0];
        await customerPortalService.updateOrder(orderToEdit.id, {
          stockCardId: formData.stockCardId,
          color: item.color,
          quantity: item.quantity,
          unitId: formData.unitId,
          mediantegOrderNo: formData.mediantegOrderNo,
          mediantegModelNo: formData.mediantegModelNo,
          mediantegOrderType: formData.mediantegOrderType,
          mediantegImageUrl: item.imageUrl || null,
          description: formData.description
        });
        toast.success('Sipariş başarıyla güncellendi');
      } else {
        // Yeni sipariş oluştur - Her renk için ayrı sipariş oluştur
        const orderPromises = formData.items.map(item => 
          customerPortalService.post('/orders', {
            stockCardId: formData.stockCardId,
            color: item.color,
            quantity: item.quantity,
            unitId: formData.unitId,
            mediantegOrderNo: formData.mediantegOrderNo,
            mediantegModelNo: formData.mediantegModelNo,
            mediantegOrderType: formData.mediantegOrderType,
            mediantegImageUrl: item.imageUrl || null, // Sadece renge özel görsel varsa gönder
            description: formData.description
          })
        );

        await Promise.all(orderPromises);
        toast.success(`${formData.items.length} sipariş başarıyla oluşturuldu`);
      }
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.message || (isEditMode ? 'Sipariş güncellenemedi' : 'Sipariş oluşturulamadı');
      toast.error(errorMessage);
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const selectedStockCard = stockCards.find(sc => sc.id === parseInt(formData.stockCardId));
  const selectedUnit = selectedStockCard?.unit || selectedStockCard?.unitType;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between z-10">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEditMode ? 'Sipariş Düzenle' : 'Yeni Sipariş Oluştur'}
            </h2>
            <div className="flex items-center gap-2">
              {!isEditMode && (
                <button
                  onClick={() => setShowMediantegSearch(true)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-xs font-medium"
                  title="Medianteg sipariş ara"
                >
                  <Search className="w-3.5 h-3.5" />
                  <span>Janes Siparişi Seç</span>
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                <X className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>

          {/* Medianteg Sipariş Bilgileri - En Üstte */}
          {formData.mediantegOrderNo && (
            <div className="px-4 pt-4 pb-2 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Medianteg Sipariş No: <span className="text-blue-600 dark:text-blue-400">{formData.mediantegOrderNo}</span>
                    </p>
                    {formData.mediantegModelNo && (
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                        Model No: <span className="text-blue-600 dark:text-blue-400">{formData.mediantegModelNo}</span>
                      </p>
                    )}
                    {formData.mediantegOrderType && (
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                        Sipariş Tipi: <span className="text-blue-600 dark:text-blue-400">{formData.mediantegOrderType}</span>
                      </p>
                    )}
                  </div>
                </div>
                {formData.mediantegImageUrl && (
                  <img
                    src={formData.mediantegImageUrl}
                    alt="Sipariş görseli"
                    className="w-12 h-12 object-cover rounded border border-gray-200 dark:border-gray-700"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {errors.general && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                <p className="text-sm text-red-800 dark:text-red-200">{errors.general}</p>
              </div>
            )}

            {/* Stok Kartı */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Stok Kartı <span className="text-red-500">*</span>
              </label>
              {loadingStockCards ? (
                <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm">
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Yükleniyor...
                </div>
              ) : (
                <SearchableSelect
                  options={stockCards}
                  value={formData.stockCardId}
                  onChange={handleStockCardChange}
                  placeholder="Stok kartı seçin"
                  searchPlaceholder="Stok kartı ara..."
                  getOptionLabel={(card) => card.name}
                  getOptionValue={(card) => card.id}
                  name="stockCardId"
                  className={errors.stockCardId ? 'border-red-300 dark:border-red-600' : ''}
                  error={!!errors.stockCardId}
                  required
                />
              )}
              {errors.stockCardId && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.stockCardId}</p>
              )}
            </div>


            {/* Renk ve Miktar Listesi */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Renk ve Miktar <span className="text-red-500">*</span>
                </label>
                {!isEditMode && (
                  <button
                    type="button"
                    onClick={addColorItem}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    <span>Renk Ekle</span>
                  </button>
                )}
              </div>

              <div className="space-y-2.5">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="flex-1">
                      <div className="relative">
                        <ColorInput
                          value={item.color}
                          onChange={(e) => updateColorItem(index, 'color', e.target.value)}
                          placeholder="Renk"
                          showColorDot={!item.imageUrl}
                          className={`w-full px-2.5 py-2 text-sm ${item.imageUrl ? 'pr-14' : ''} border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                            errors[`items.${index}.color`] ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        />
                        {item.imageUrl && (
                          <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none z-10 flex-shrink-0">
                            {item.color?.trim() ? (
                              <ColorDot colorName={item.color} size="sm" />
                            ) : null}
                            <div className="w-7 h-7 rounded border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
                              <img
                                src={item.imageUrl}
                                alt={item.color || 'Renk görseli'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      {errors[`items.${index}.color`] && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                          {errors[`items.${index}.color`]}
                        </p>
                      )}
                    </div>
                    <div className="w-28">
                      <div className="relative">
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={item.quantity}
                          onChange={(e) => updateColorItem(index, 'quantity', e.target.value)}
                          placeholder="Miktar"
                          className={`w-full px-2.5 py-2 text-sm pr-10 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                            errors[`items.${index}.quantity`] ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        />
                        {selectedUnit && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">
                            {selectedUnit.shortName || selectedUnit.name}
                          </span>
                        )}
                      </div>
                      {errors[`items.${index}.quantity`] && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                          {errors[`items.${index}.quantity`]}
                        </p>
                      )}
                    </div>
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeColorItem(index)}
                        className="mt-0.5 p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Açıklama */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Açıklama (Opsiyonel)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white resize-none"
                placeholder="Ek açıklama girin"
              />
            </div>

            {/* Kaydet Butonu */}
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700 -mx-5 px-5">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>{isEditMode ? 'Güncelleniyor...' : 'Oluşturuluyor...'}</span>
                  </>
                ) : (
                  <span>{isEditMode ? 'Siparişi Güncelle' : 'Sipariş Oluştur'}</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Medianteg Sipariş Arama Modalı */}
      {showMediantegSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            {mediantegOrderDetailLoading && (
              <div
                className="absolute inset-0 z-[65] flex flex-col items-center justify-center gap-3 rounded-lg bg-white/90 dark:bg-gray-900/90 backdrop-blur-[2px]"
                aria-busy="true"
                aria-live="polite"
              >
                <Loader className="w-10 h-10 text-blue-600 dark:text-blue-400 animate-spin" />
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 px-4 text-center">
                  Sipariş bilgileri yükleniyor...
                </p>
              </div>
            )}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-[1]">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Medianteg Sipariş Ara
              </h3>
              <button
                type="button"
                onClick={() => {
                  if (mediantegOrderDetailLoading) return;
                  setShowMediantegSearch(false);
                  setMediantegSearchTerm('');
                  setMediantegSearchResults([]);
                }}
                disabled={mediantegOrderDetailLoading}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={mediantegSearchTerm}
                  onChange={(e) => setMediantegSearchTerm(e.target.value)}
                  placeholder="Sipariş no veya model no girin..."
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {searchingMedianteg && (
                <div className="flex items-center justify-center py-4">
                  <Loader className="w-6 h-6 text-blue-400 animate-spin" />
                </div>
              )}

              {!searchingMedianteg && mediantegSearchResults.length > 0 && (
                <div className="space-y-2">
                  {mediantegSearchResults.map((order, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => loadMediantegOrderDetails(order.orderId)}
                      disabled={mediantegOrderDetailLoading}
                      className="w-full text-left p-4 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-wait disabled:pointer-events-none"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                          {order.imageUrl && (
                            <img
                              src={order.imageUrl}
                              alt="Sipariş görseli"
                              className="w-16 h-16 object-cover rounded border border-gray-200 dark:border-gray-700 flex-shrink-0"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <p className="font-medium text-gray-900 dark:text-white">
                            {order.modelNo}/{order.orderNo}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!searchingMedianteg && mediantegSearchTerm && mediantegSearchResults.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Sonuç bulunamadı
                </div>
              )}

              {!searchingMedianteg && !mediantegSearchTerm && mediantegSearchResults.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Aktif sipariş bulunamadı
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateOrderForm;
